import {
  useCallback,
  useMemo,
  useRef,
} from 'react';
import * as is from 'is';
import { saveStream } from '@utils/graphql/creators/streams';
import { useSourceConverter } from './source';
import { useZoneConverter } from './zone';
import { isYesValue } from './utils';

export const useStreamConverter = () => {
  const [
    createSource,
    retrieveSource, // eslint-disable-line no-unused-vars
    streamProfileHeaderColumns,
  ] = useSourceConverter();
  const [
    createZone,
    retrieveZone, // eslint-disable-line no-unused-vars
    zoneHeaderColumns,
  ] = useZoneConverter();
  const savedStreams = useRef(new Map());
  const headerMapping = useMemo(
    () => {
      return {
        'Stream Name': {
          property: 'name',
        },
        'Stream Description': {
          property: 'description',
        },
        'Stream On Demand': {
          property: 'pullOnRequest',
          processor(row, input, value) {
            return isYesValue(value, false);
          },
        },
        'Stream RTSP Interleaved': {
          property: 'useInterleavedRTSPTransport',
          processor(row, input, value) {
            return isYesValue(value, false);
          },
        },
        'Stream Profile ID': {
          property: 'sourceId',
          processor(row, input, value) {
            if (is.empty(value)) {
              return;
            }
            return value;
          },
        },
        'Stream Host Appliance ID': {
          property: 'applianceId',
          processor(row, input, value) {
            if (is.empty(value)) {
              return;
            }
            return value;
          },
        },
        'Stream Zone ID': {
          property: 'zoneId',
          processor(row, input, value) {
            if (is.empty(value)) {
              return;
            }
            return value;
          },
        },
        'Zone Name': {
          property: 'zoneId',
          async processor(row, input, value) {
            if (!is.undef(input.zoneId)) {
              return;
            }
            if (is.empty(value)) {
              return;
            }
            const data = await createZone(row);
            return data.id;
          },
        },
        'Stream Profile Name': {
          property: 'sourceId',
          async processor(row, input, value) {
            if (!is.undef(input.sourceId)) {
              return;
            }
            if (is.empty(value)) {
              return;
            }
            const data = await createSource(row);
            return data.id;
          },
        },
      };
    },
    [createSource, createZone]
  );
  const headerColumns = useMemo(
    () => Array.from(new Set(Object.keys(headerMapping).concat(streamProfileHeaderColumns, zoneHeaderColumns))),
    [
      headerMapping,
      streamProfileHeaderColumns,
      zoneHeaderColumns,
    ]
  );
  const getMapping = useCallback(
    (columnName) => {
      const response = headerMapping[columnName];
      if (is.object(response)) {
        return response;
      }
    },
    [headerMapping]
  );
  const getReverseMapping = useCallback(
    (property) => {
      const columnNames = [];
      for (const [columnName, properties] of Object.entries(headerMapping)) {
        if (properties.property === property) {
          columnNames.push(columnName);
        }
      }
      return columnNames;
    },
    [headerMapping]
  );
  const storeStream = useCallback(
    (device) => {
      if (savedStreams.current.has(device.name)) {
        return;
      }
      savedStreams.current.set(device.name, device);
    },
    [savedStreams]
  );
  const retrieveStream = useCallback(
    async (name) => {
      if (is.undef(name)) {
        return;
      }
      if (savedStreams.current.has(name)) {
        return savedStreams.current.get(name);
      }
    },
    [savedStreams]
  );
  const convert = useCallback(
    async (row) => {
      const input = {};
      for (const [header, value] of Object.entries(headerMapping)) {
        const { property, processor } = value;
        const rowValue = row[header];
        const _value = is.function(processor)
          ? await processor(row, input, rowValue)
          : rowValue;

        if (_value === undefined) {
          continue;
        }
        input[property] = _value;
      }
      return input;
    },
    [headerMapping]
  );
  const createStream = useCallback(
    async (row) => {
      const names = getReverseMapping('name');
      for (const headerName of names) {
        const stream = await retrieveStream(row[headerName]);
        if (is.defined(stream)) {
          return stream;
        }
      }
      const input = await convert(row, getMapping);
      const { mutation, execute } = saveStream(undefined, input);
      mutation.addReturnProperties('id', 'name');
      const response = await execute();
      if (response.error) {
        throw new Error(response.error);
      }
      storeStream(response.data);
      return response.data;
    },
    [
      storeStream,
      retrieveStream,
      getReverseMapping,
      getMapping,
      convert,
    ]
  );
  return [
    createStream,
    retrieveStream,
    headerMapping,
    headerColumns,
  ];
};